import { FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import PageHeaderWithBack from '../../components/PageHeaderWithBack';
import { OrderResponse, OrderStatus, fetchOrder, fetchPublicCompanyConfig } from '../../api/orders';
import { fetchOrderFiles, type OrderFileRecord } from '../../api/files';
import { updateOrder } from '../../api/orders';
import { getAiHealthScoreStyles } from '../../utils/aiUtils';
import { statusBadgeCx } from '../../utils/statusBadges';

const STATUS_LABELS: Record<OrderStatus, string> = {
  initiated: 'Initiated',
  draft: 'Draft',
  approved: 'Approved',
  confirmed: 'Confirmed',
  in_progress: 'In progress',
  completed: 'Completed',
  verified: 'Verified',
  done: 'Done',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) {
    return '—';
  }
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
};

const formatAmount = (value: string | number | null | undefined): string => {
  const numeric = typeof value === 'number' ? value : Number(value ?? 0);
  if (Number.isNaN(numeric)) {
    return '0.00';
  }
  return numeric.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatRelative = (target: Date): string => {
  const now = new Date();
  let future = target.getTime() >= now.getTime();
  const start = future ? now : target;
  const end = future ? target : now;

  // Rough calc for Y/M/D components
  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();
  let days = end.getDate() - start.getDate();

  if (days < 0) {
    // borrow days from previous month
    const prevMonth = new Date(end.getFullYear(), end.getMonth(), 0);
    days += prevMonth.getDate();
    months -= 1;
  }
  if (months < 0) {
    months += 12;
    years -= 1;
  }

  const parts: string[] = [];
  if (years > 0) parts.push(`${years} year${years === 1 ? '' : 's'}`);
  if (months > 0) parts.push(`${months} month${months === 1 ? '' : 's'}`);
  if (days > 0 || parts.length === 0) parts.push(`${days} day${days === 1 ? '' : 's'}`);

  const phrase = parts.slice(0, 2).join(' '); // keep it short
  return future ? `in ${phrase}` : `${phrase} ago`;
};

const formatUser = (
  user?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null,
): string | undefined => {
  if (!user) {
    return undefined;
  }
  const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return name || user.email || undefined;
};

interface TimelineEvent {
  key: string;
  title: string;
  dateLabel: string;
  description?: string;
  note?: string;
}

const OrderView: FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string })?.from;
  const [order, setOrder] = useState<OrderResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelNotes, setCancelNotes] = useState('');
  const [cancelSaving, setCancelSaving] = useState(false);
  const [filesInspection, setFilesInspection] = useState<OrderFileRecord[]>([]);
  const [filesTechnician, setFilesTechnician] = useState<OrderFileRecord[]>([]);
  const [filesDelivery, setFilesDelivery] = useState<OrderFileRecord[]>([]);
  const [currencySymbol, setCurrencySymbol] = useState<string>('');

  useEffect(() => {
    if (!orderId) {
      setError('Order not found.');
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    fetchOrder(orderId)
      .then((response) => {
        if (!active) {
          return;
        }
        setOrder(response);
      })
      .catch((err) => {
        if (!active) {
          return;
        }
        const message = err instanceof Error ? err.message : 'Failed to load order details.';
        setError(message);
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [orderId]);

  useEffect(() => {
    if (!orderId) return;
    fetchOrderFiles(orderId, 'inspection').then(setFilesInspection).catch(() => setFilesInspection([]));
    fetchOrderFiles(orderId, 'technician').then(setFilesTechnician).catch(() => setFilesTechnician([]));
    fetchOrderFiles(orderId, 'delivery').then(setFilesDelivery).catch(() => setFilesDelivery([]));
  }, [orderId]);

  // Load public company config to get currency symbol for invoice print
  useEffect(() => {
    fetchPublicCompanyConfig()
      .then((res) => { if (res?.ok && res.data?.currencySymbol) setCurrencySymbol(res.data.currencySymbol || ''); })
      .catch(() => { });
  }, []);

  const printInvoice = () => {
    if (!order) return;
    const symbol = currencySymbol || '';
    const fmt = (v: unknown) => {
      const n = typeof v === 'number' ? v : Number(v ?? 0);
      if (Number.isNaN(n)) return symbol ? `${symbol} 0.00` : '0.00';
      const formatted = n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return symbol ? `${symbol} ${formatted}` : formatted;
    };
    const locationName = (order as any)?.location?.name || order.locationCode || '';
    const customerName = order.customer ? `${order.customer.firstName ?? ''} ${order.customer.lastName ?? ''}`.trim() || (order.customer.email || '') : '';
    const customerContact = order.customer?.mobileNumber || order.customer?.email || '';
    const vehicleLine = order.customerVehicle ? [order.customerVehicle.vehicleBrand?.name, order.customerVehicle.vehicleModel?.name].filter(Boolean).join(' · ') : '';
    const created = order.createdAt ? new Date(order.createdAt).toLocaleString() : '';

    const rows = (order.items || []).map((it) => `
      <tr>
        <td>${(it.description || it.productVariant?.name || 'Item')}</td>
        <td class="num">${it.quantity}</td>
        <td class="num">${fmt(it.unitPrice)}</td>
        <td class="num">${fmt(it.discountTotal)}</td>
        <td class="num">${fmt(it.taxTotal)}</td>
        <td class="num">${fmt(it.lineTotal)}</td>
      </tr>
    `).join('');

    const html = `<!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Invoice ${order.orderNumber}</title>
        <style>
          body{font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif; color:#0f172a;}
          .wrap{max-width:900px;margin:24px auto;padding:24px;border:1px solid #e2e8f0;border-radius:12px}
          h1{margin:0 0 4px 0;font-size:20px}
          .muted{color:#64748b;font-size:12px}
          .grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:8px}
          table{width:100%;border-collapse:collapse;margin-top:16px;font-size:12px}
          th,td{padding:8px;border-bottom:1px solid #e2e8f0}
          th{background:#f8fafc;text-align:left}
          td.num, th.num{text-align:right}
          .totals{max-width:320px;margin-left:auto;margin-top:12px;font-size:13px}
          .totals div{display:flex;justify-content:space-between;margin:4px 0}
          .grand{font-weight:700;font-size:14px}
          @media print {.wrap{border:none}}
        </style>
      </head>
      <body>
        <div class="wrap">
          <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px">
            <div>
              <h1>Invoice</h1>
              <div class="muted">Order #: ${order.orderNumber}</div>
              <div class="muted">Created: ${created}</div>
              <div class="muted">Location: ${locationName}</div>
            </div>
            <div style="text-align:right">
              <div><strong>Customer</strong></div>
              <div class="muted">${customerName || '—'}</div>
              <div class="muted">${customerContact || ''}</div>
              <div class="muted">${vehicleLine || ''}</div>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th class="num">Qty</th>
                <th class="num">Unit</th>
                <th class="num">Discount</th>
                <th class="num">Tax</th>
                <th class="num">Total</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
          <div class="totals">
            <div><span>Subtotal</span><span>${fmt(order.subtotalAmount)}</span></div>
            <div><span>Discount</span><span>${fmt(order.discountTotal)}</span></div>
            <div><span>Tax</span><span>${fmt(order.taxTotal)}</span></div>
            <div class="grand"><span>Grand total</span><span>${fmt(order.grandTotal)}</span></div>
          </div>
        </div>
        <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 200); };</script>
      </body>
    </html>`;
    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const metadata = order?.metadata as Record<string, unknown> | undefined;
  const erpPaymentUpdatedAt =
    typeof metadata?.['erpPaymentUpdatedAt'] === 'string'
      ? (metadata?.['erpPaymentUpdatedAt'] as string)
      : null;
  const erpPaymentStatus =
    typeof metadata?.['erpPaymentStatus'] === 'string'
      ? (metadata?.['erpPaymentStatus'] as string)
      : undefined;
  const erpPaymentDone = Boolean(order?.erpId);

  const timelineEvents = useMemo<TimelineEvent[]>(() => {
    if (!order) {
      return [];
    }

    const events: TimelineEvent[] = [];

    events.push({
      key: 'created',
      title: 'Order created',
      dateLabel: formatDateTime(order.createdAt),
      description: formatUser(order.createdBy) ? `Created by ${formatUser(order.createdBy)}` : undefined,
    });

    events.push({
      key: 'status',
      title: `Current status – ${STATUS_LABELS[order.status] ?? order.status}`,
      dateLabel: formatDateTime(order.updatedAt),
    });

    events.push({
      key: 'managerApproval',
      title: order.managerApproved ? 'Manager approved' : 'Manager approval pending',
      dateLabel: formatDateTime(order.managerApprovedAt),
      description: formatUser(order.managerApprovedBy)
        ? `Manager: ${formatUser(order.managerApprovedBy)}`
        : undefined,
      note: order.managerApprovalNotes ?? undefined,
    });

    events.push({
      key: 'customerApproval',
      title: order.customerApproved ? 'Customer approved' : 'Customer approval pending',
      dateLabel: formatDateTime(order.customerApprovedAt),
      description: formatUser(order.customerApprovedBy)
        ? `Customer contact: ${formatUser(order.customerApprovedBy)}`
        : undefined,
      note: order.customerNotes ?? undefined,
    });

    if (order.technicians && order.technicians.length > 0) {
      const technicianNames = order.technicians
        .map((tech) => {
          const friendly = formatUser(tech);
          if (friendly) {
            return friendly;
          }
          if ('username' in tech && typeof tech.username === 'string' && tech.username) {
            return tech.username;
          }
          if ('email' in tech && typeof tech.email === 'string' && tech.email) {
            return tech.email;
          }
          return '';
        })
        .filter(Boolean)
        .join(', ');

      events.push({
        key: 'technicians',
        title: 'Technicians assigned',
        dateLabel: order.technicianCompletedTheJobAt
          ? formatDateTime(order.technicianCompletedTheJobAt)
          : 'Assignment time not available',
        description: technicianNames ? `Assigned technicians: ${technicianNames}` : undefined,
        note: order.technicianNotes ?? undefined,
      });
    }

    // Always show ERP payment update status based on presence of ERP ID
    events.push({
      key: 'erpPayment',
      title: 'ERP payment update',
      dateLabel: formatDateTime(erpPaymentUpdatedAt ?? order.updatedAt),
      description: `Status: ${erpPaymentDone ? 'DONE' : 'PENDING'}`,
    });

    return events;
  }, [erpPaymentStatus, erpPaymentUpdatedAt, erpPaymentDone, order]);

  const customerCard = useMemo(() => {
    if (!order?.customer) {
      return null;
    }
    const customer = order.customer;
    const fullName = `${customer.firstName ?? ''} ${customer.lastName ?? ''}`.trim();
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
        <h3 className="text-base font-semibold text-slate-700">Customer</h3>
        <dl className="mt-3 space-y-2 text-sm">
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Name</dt>
            <dd className="text-slate-700">{fullName || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Email</dt>
            <dd className="text-slate-700">{customer.email || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Phone</dt>
            <dd className="text-slate-700">{customer.mobileNumber || '—'}</dd>
          </div>
        </dl>
      </div>
    );
  }, [order?.customer]);

  const vehicleCard = useMemo(() => {
    if (!order?.customerVehicle) {
      return null;
    }
    const vehicle = order.customerVehicle;
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
        <h3 className="text-base font-semibold text-slate-700">Vehicle</h3>
        <dl className="mt-3 space-y-2 text-sm">
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Brand</dt>
            <dd className="text-slate-700">{vehicle.vehicleBrand?.name || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Model</dt>
            <dd className="text-slate-700">{vehicle.vehicleModel?.name || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Registration</dt>
            <dd className="text-slate-700">{vehicle.vehicleRegistrationNumber || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">VIN</dt>
            <dd className="text-slate-700">{vehicle.vehicleVin || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Chassis No</dt>
            <dd className="text-slate-700">{vehicle.vehicleChassisNumber || '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Year</dt>
            <dd className="text-slate-700">{vehicle.vehicleRegistrationYear || '—'}</dd>
          </div>
        </dl>
      </div>
    );
  }, [order?.customerVehicle]);

  const financialCard = useMemo(() => {
    if (!order) {
      return null;
    }
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
        <h3 className="text-base font-semibold text-slate-700">Financial summary</h3>
        <dl className="mt-3 space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Subtotal</dt>
            <dd className="text-slate-700">{order.subtotalAmount ?? '—'}</dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Discounts</dt>
            <dd className="text-slate-700">{order.discountTotal ?? '—'}</dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Tax</dt>
            <dd className="text-slate-700">{order.taxTotal ?? '—'}</dd>
          </div>
          <div className="flex items-center justify-between border-t border-slate-200 pt-2">
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Grand total</dt>
            <dd className="text-base font-semibold text-slate-800">{order.grandTotal ?? '—'}</dd>
          </div>
        </dl>
        {order.paymentSummaries && order.paymentSummaries.length > 0 ? (
          <div className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Payments</p>
            <ul className="mt-2 divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white">
              {order.paymentSummaries.map((p) => (
                <li key={p.id} className="flex items-center justify-between px-3 py-2">
                  <div>
                    <div className="text-slate-700">{p.paymentTypeName}</div>
                    <div className="text-xs text-slate-400">{formatDateTime(p.happenedAt)}</div>
                  </div>
                  <div className="text-sm font-semibold text-slate-800">{formatAmount(p.amount)}</div>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    );
  }, [order]);

  const notesCard = useMemo(() => {
    if (!order || (!order.managerApprovalNotes && !order.technicianNotes && !order.customerNotes)) {
      return null;
    }
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
        <h3 className="text-base font-semibold text-slate-700">Notes</h3>
        <div className="mt-3 space-y-3">
          {order.managerApprovalNotes ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Manager notes</p>
              <p className="mt-1 whitespace-pre-line text-slate-700">{order.managerApprovalNotes}</p>
            </div>
          ) : null}
          {order.technicianNotes ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Technician notes</p>
              <p className="mt-1 whitespace-pre-line text-slate-700">{order.technicianNotes}</p>
            </div>
          ) : null}
          {order.customerNotes ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Customer notes</p>
              <p className="mt-1 whitespace-pre-line text-slate-700">{order.customerNotes}</p>
            </div>
          ) : null}
        </div>
      </div>
    );
  }, [order]);

  const aiCard = useMemo(() => {
    const analysis = (order as any)?.aiAnalysis ?? null;
    if (!analysis) {
      return null;
    }
    const scores = (analysis as any).scores ?? {};
    const overall = scores.overall ?? {};
    const sections = scores.sections ?? {};
    const recs = (analysis as any).recommendations ?? {};
    const now = (recs.recommendedNow?.products ?? []) as Array<{
      id: string;
      name: string;
      reason: string;
    }>;
    const future = (recs.recommendedFuture?.products ?? []) as Array<{
      id: string;
      name: string;
      reason: string;
    }>;

    const sectionKeys: Array<keyof typeof sections> = [
      'tyres',
      'engine',
      'brakes',
      'suspension',
      'electrical',
      'body',
    ];

    const styles = getAiHealthScoreStyles(overall.score);

    return (
      <div className={`space-y-4 rounded-2xl border p-4 text-sm ${styles.container}`}>
        <div>
          <h3 className={`text-base font-semibold ${styles.text}`}>AI vehicle health summary</h3>
          <p className={`mt-2 text-sm ${styles.text}`}>
            <span className="font-semibold">
              {overall.score ?? '—'}
            </span>{' '}
            <span className={`text-xs uppercase tracking-wide ${styles.label}`}>
              {overall.label ?? ''}
            </span>
            {overall.summary ? (
              <span className={`ml-1 ${styles.label}`}>
                — {overall.summary}
              </span>
            ) : null}
          </p>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Section scores
          </p>
          <div className="mt-2 grid gap-3 md:grid-cols-2">
            {sectionKeys.map((key) => {
              const keyName = String(key);
              const sec = (sections as any)[keyName];
              if (!sec) return null;
              return (
                <div
                  key={keyName}
                  className="rounded-lg border border-slate-200 bg-white p-3"
                >
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-slate-800">
                      {keyName.replace(/^\w/, (c) => c.toUpperCase())}
                    </span>
                    <span className="text-xs font-medium text-slate-600">
                      {sec.score ?? '—'} • {sec.label ?? ''}
                    </span>
                  </div>
                  {sec.summary ? (
                    <p className="mt-1 text-xs text-slate-700">
                      {sec.summary}
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Recommended now
          </p>
          {now.length === 0 ? (
            <p className="mt-1 text-xs text-slate-600">
              No immediate recommendations.
            </p>
          ) : (
            <ul className="mt-1 space-y-1 text-sm">
              {now.map((item) => (
                <li key={item.id} className="rounded-md bg-white/80 px-2 py-1">
                  <div className="font-medium text-slate-800">{item.name}</div>
                  {item.reason ? (
                    <div className="text-[11px] text-slate-600">{item.reason}</div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Recommended in future
          </p>
          {future.length === 0 ? (
            <p className="mt-1 text-xs text-slate-600">
              No future recommendations.
            </p>
          ) : (
            <ul className="mt-1 space-y-1 text-sm">
              {future.map((item) => (
                <li key={item.id} className="rounded-md bg-white/80 px-2 py-1">
                  <div className="font-medium text-slate-800">{item.name}</div>
                  {item.reason ? (
                    <div className="text-[11px] text-slate-600">{item.reason}</div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }, [order]);

  return (
    <div className="rounded-[28px] border border-slate-200/70 bg-white/95 p-8 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)] min-h-[calc(100vh-220px)]">
      <PageHeaderWithBack
        title={order ? (
          <span className="inline-flex items-center gap-3">
            <span>Order {order.orderNumber}</span>
            <span className={statusBadgeCx(order.status)}>{STATUS_LABELS[order.status] ?? order.status}</span>
          </span>
        ) : 'View order'}
        description="View status updates, customer details, and vehicle information for this order."
        onBack={() => navigate(from || '/order-management')}
        backLabel={
          from?.includes('/crm/view/')
            ? 'Back to customer'
            : from?.includes('/order-management/pending')
              ? 'Back to pending'
              : from
                ? 'Back to list'
                : 'Back to orders'
        }
      />

      {error ? <p className="mt-4 text-sm text-danger">{error}</p> : null}

      {loading ? (
        <p className="mt-6 text-sm text-slate-500">Loading order details…</p>
      ) : order ? (
        <div className="mt-6 grid gap-6 lg:grid-cols-[2fr_1fr]">
          <div className="space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
              <h3 className="text-base font-semibold text-slate-700">Timeline</h3>
              <div className="mt-4 relative">
                <ul className="space-y-6">
                  {timelineEvents.map((event, index) => (
                    <li key={event.key} className="relative pl-8">
                      <span className="absolute left-0 top-1 flex h-3 w-3 items-center justify-center rounded-full border border-primary bg-white" />
                      <div>
                        <p className="text-sm font-semibold text-slate-700">{event.title}</p>
                        <p className="text-xs text-slate-400">{event.dateLabel}</p>
                        {event.description ? (
                          <p className="mt-1 text-sm text-slate-600">{event.description}</p>
                        ) : null}
                        {event.note ? (
                          <p className="mt-2 whitespace-pre-line text-sm text-slate-500">{event.note}</p>
                        ) : null}
                      </div>
                      {index !== timelineEvents.length - 1 ? (
                        <div className="absolute left-[2px] top-4 bottom-[-24px] w-px bg-slate-200" aria-hidden />
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {order?.items && order.items.length > 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
                <h3 className="text-base font-semibold text-slate-700">Order items</h3>
                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full table-fixed border-separate border-spacing-y-2">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
                        <th className="px-3 py-2 font-semibold">Item</th>
                        <th className="px-3 py-2 font-semibold w-20 text-right">Qty</th>
                        <th className="px-3 py-2 font-semibold w-28 text-right">Unit</th>
                        <th className="px-3 py-2 font-semibold w-28 text-right">Subtotal</th>
                        <th className="px-3 py-2 font-semibold w-28 text-right">Discount</th>
                        <th className="px-3 py-2 font-semibold w-28 text-right">Tax</th>
                        <th className="px-3 py-2 font-semibold w-28 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.items
                        .slice()
                        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                        .map((item) => {
                          const name = item.productVariant?.name || item.description || '—';
                          const nextDueDays =
                            typeof (item as any).nextDueAfterDays === 'number'
                              ? (item as any).nextDueAfterDays as number
                              : null;
                          let itemCompletedAt: Date | null = null;
                          if (Array.isArray(item.serviceTechnicians) && item.serviceTechnicians.length) {
                            const dates = item.serviceTechnicians
                              .map((st) => (st.completedAt ? new Date(st.completedAt) : null))
                              .filter((d): d is Date => !!d && !Number.isNaN(d.getTime()));
                            if (dates.length) {
                              itemCompletedAt = new Date(Math.max(...dates.map((d) => d.getTime())));
                            }
                          }
                          let nextDueLabel: string | null = null;
                          if (nextDueDays !== null) {
                            if (itemCompletedAt) {
                              const next = new Date(itemCompletedAt);
                              next.setDate(next.getDate() + nextDueDays);
                              nextDueLabel = formatRelative(next);
                            } else {
                              const future = new Date();
                              future.setDate(future.getDate() + nextDueDays);
                              nextDueLabel = formatRelative(future);
                            }
                          }
                          return (
                            <tr key={item.id} className="bg-slate-50/60">
                              <td className="px-3 py-2 align-top">
                                <div className="text-slate-800">{name}</div>
                                {item.itemType ? (
                                  <div className="text-xs text-slate-400 mt-0.5">{item.itemType}</div>
                                ) : null}
                                {nextDueLabel ? (
                                  <div className="text-xs text-amber-600 mt-0.5">Next due: {nextDueLabel}</div>
                                ) : null}
                              </td>
                              <td className="px-3 py-2 text-right align-top">{item.quantity ?? '—'}</td>
                              <td className="px-3 py-2 text-right align-top">{item.unitPrice ?? '—'}</td>
                              <td className="px-3 py-2 text-right align-top">{item.lineSubtotal ?? '—'}</td>
                              <td className="px-3 py-2 text-right align-top">{item.discountTotal ?? '—'}</td>
                              <td className="px-3 py-2 text-right align-top">{item.taxTotal ?? '—'}</td>
                              <td className="px-3 py-2 text-right align-top font-semibold text-slate-800">{item.lineTotal ?? '—'}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}

            {aiCard}

            {notesCard}
          </div>

          <aside className="space-y-6">
            {customerCard}
            {vehicleCard}
            {financialCard}
            <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
              <h3 className="text-base font-semibold text-slate-700">Files</h3>
              <div className="mt-3 space-y-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Inspection</div>
                  {filesInspection.length === 0 ? (
                    <div className="text-xs text-slate-400 mt-1">No files</div>
                  ) : (
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {filesInspection.map((f) => {
                        const isVideo = (f.contentType || '').startsWith('video/');
                        return (
                          <a key={f.id} href={f.url} target="_blank" rel="noreferrer" className="rounded-lg border border-slate-200 p-1 bg-white">
                            {isVideo ? (
                              <video src={f.url} className="aspect-square w-full object-cover rounded" muted playsInline />
                            ) : (
                              <img src={f.url} alt={f.originalName ?? ''} className="aspect-square w-full object-cover rounded" />
                            )}
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Technician</div>
                  {filesTechnician.length === 0 ? (
                    <div className="text-xs text-slate-400 mt-1">No files</div>
                  ) : (
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {filesTechnician.map((f) => {
                        const isVideo = (f.contentType || '').startsWith('video/');
                        return (
                          <a key={f.id} href={f.url} target="_blank" rel="noreferrer" className="rounded-lg border border-slate-200 p-1 bg-white">
                            {isVideo ? (
                              <video src={f.url} className="aspect-square w-full object-cover rounded" muted playsInline />
                            ) : (
                              <img src={f.url} alt={f.originalName ?? ''} className="aspect-square w-full object-cover rounded" />
                            )}
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Pre-delivery</div>
                  {filesDelivery.length === 0 ? (
                    <div className="text-xs text-slate-400 mt-1">No files</div>
                  ) : (
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {filesDelivery.map((f) => {
                        const isVideo = (f.contentType || '').startsWith('video/');
                        return (
                          <a key={f.id} href={f.url} target="_blank" rel="noreferrer" className="rounded-lg border border-slate-200 p-1 bg-white">
                            {isVideo ? (
                              <video src={f.url} className="aspect-square w-full object-cover rounded" muted playsInline />
                            ) : (
                              <img src={f.url} alt={f.originalName ?? ''} className="aspect-square w-full object-cover rounded" />
                            )}
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-[11px] leading-snug text-slate-500 italic">
              Disclaimer: Checklists and media attached to this order reflect visual/operational checks at the time
              of assessment and do not constitute a full diagnostic. Certain faults may be hidden or intermittent and
              may not be detectable. The workshop is not liable for issues that were not reasonably identifiable during
              inspection or at handover.
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
              <h3 className="text-base font-semibold text-slate-700">Order metadata</h3>
              <dl className="mt-3 space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">ERP ID</dt>
                  <dd className="text-slate-700">{order.erpId ?? '—'}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Counter code</dt>
                  <dd className="text-slate-700">{order.counterCode ?? '—'}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Location code</dt>
                  <dd className="text-slate-700">{order.locationCode ?? '—'}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">ERP payment update</dt>
                  <dd>
                    {erpPaymentDone ? (
                      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">DONE</span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">PENDING</span>
                    )}
                  </dd>
                </div>
                {erpPaymentUpdatedAt ? (
                  <div className="flex items-center justify-between">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">ERP payment updated</dt>
                    <dd className="text-slate-700">{formatDateTime(erpPaymentUpdatedAt)}</dd>
                  </div>
                ) : null}
              </dl>
              <div className="mt-4 flex justify-between gap-2">
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                  onClick={printInvoice}
                >
                  Print invoice (PDF)
                </button>
                {order.status !== 'cancelled' && order.status !== 'done' && order.status !== 'delivered' ? (
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-lg border border-danger/30 px-3 py-1.5 text-sm font-semibold text-danger transition hover:bg-danger/10"
                    onClick={() => setCancelOpen(true)}
                  >
                    Cancel order
                  </button>
                ) : null}
              </div>
            </div>
          </aside>
        </div>
      ) : null}

      {cancelOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
          <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-4">
            <h3 className="text-base font-semibold text-slate-800">Cancel order</h3>
            <p className="mt-2 text-sm text-slate-600">Please provide a reason for cancellation (optional).</p>
            <textarea
              className="mt-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              rows={4}
              value={cancelNotes}
              onChange={(e) => setCancelNotes(e.target.value)}
              placeholder="Cancellation notes"
              disabled={cancelSaving}
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600"
                onClick={() => setCancelOpen(false)}
                disabled={cancelSaving}
              >
                Close
              </button>
              <button
                type="button"
                className="rounded-lg bg-danger px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
                onClick={async () => {
                  if (!order) return;
                  setCancelSaving(true);
                  try {
                    const updated = await updateOrder(order.id, { status: 'cancelled', cancellationNotes: cancelNotes || null });
                    setOrder(updated);
                    setCancelOpen(false);
                    setCancelNotes('');
                  } catch (e) {
                    const msg = e instanceof Error ? e.message : 'Failed to cancel order';
                    setError(msg);
                  } finally {
                    setCancelSaving(false);
                  }
                }}
                disabled={cancelSaving}
              >
                {cancelSaving ? 'Cancelling…' : 'Confirm cancel'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default OrderView;
